/* ==========================================================================
   Rook UI Core — Carousel Controller
   Web Component: <rk-carousel-element>
   Powered by Swiper.js (Lazy Loaded via CDN)
   ========================================================================== */

if (!customElements.get('rk-carousel-element')) {
    class RkCarouselElement extends HTMLElement {
        constructor() {
            super();
            this.swiper = null;
            this.options = {};
        }

        async connectedCallback() {
            await this._loadSwiper();
            this._initSwiper();

            // Resize observer for breakpoints updates/recalculation
            if (window.ResizeObserver) {
                this.resizeObserver = new ResizeObserver(() => {
                    if (this.swiper && typeof this.swiper.update === 'function') {
                        this.swiper.update();
                    }
                });
                this.resizeObserver.observe(this);
            }
        }

        disconnectedCallback() {
            if (this.swiper && typeof this.swiper.destroy === 'function') {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        }

        async _loadSwiper() {
            // Check if Swiper is already on window
            if (window.Swiper) return;

            // Wait for concurrent loads if another carousel is fetching it
            if (window.RkSwiperLoading) {
                return window.RkSwiperLoading;
            }

            window.RkSwiperLoading = new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js';

                const style = document.createElement('link');
                style.rel = 'stylesheet';
                style.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css';

                script.onload = () => resolve();
                script.onerror = reject;

                document.head.appendChild(style);
                document.head.appendChild(script);
            });

            return window.RkSwiperLoading;
        }

        _initSwiper() {
            const container = this.querySelector('.swiper');
            if (!container) return;

            // Read dataset options for customized parameters
            const rawOptions = this.getAttribute('data-swiper-options');
            if (rawOptions) {
                try {
                    this.options = JSON.parse(rawOptions);
                } catch (e) {
                    console.error('Invalid Swiper options JSON:', e);
                }
            }

            // Fallback attribute reading for clean API
            const gap = parseInt(this.getAttribute('data-gap')) || 0;
            const deskItems = parseFloat(this.getAttribute('data-desktop-items')) || 3;
            const mobileItems = parseFloat(this.getAttribute('data-mobile-items')) || 1;

            const defaultOptions = {
                slidesPerView: mobileItems,
                spaceBetween: gap,
                navigation: {
                    nextEl: this.querySelector('.swiper-button-next'),
                    prevEl: this.querySelector('.swiper-button-prev'),
                },
                pagination: {
                    el: this.querySelector('.swiper-pagination'),
                    clickable: true,
                },
                loop: this.hasAttribute('data-loop') && this.getAttribute('data-loop') !== 'false',
                autoplay: this.hasAttribute('data-autoplay') ? {
                    delay: parseInt(this.getAttribute('data-autoplay-delay')) || 3000,
                    disableOnInteraction: false,
                } : false,
                freeMode: this.hasAttribute('data-free-mode'),
                breakpoints: this.options.breakpoints || {
                    768: {
                        slidesPerView: deskItems
                    }
                },
                // Accessibility features native to Swiper
                a11y: {
                    enabled: true,
                    prevSlideMessage: 'Slide Anterior',
                    nextSlideMessage: 'Próximo Slide',
                }
            };

            // Merge passed options directly, allowing deep overrides
            const finalOptions = { ...defaultOptions, ...this.options };

            // Initialize virtual component integration
            this.swiper = new window.Swiper(container, finalOptions);

            this.dispatchEvent(new CustomEvent('rk:carousel:init', {
                bubbles: true,
                detail: { swiper: this.swiper }
            }));
        }

        /* ------------------------------------------------------------------ */
        /*  Public API methods for external buttons / programmatic flow     */
        /* ------------------------------------------------------------------ */
        slideNext() {
            if (this.swiper) this.swiper.slideNext();
        }

        slidePrev() {
            if (this.swiper) this.swiper.slidePrev();
        }

        slideTo(index) {
            if (this.swiper) this.swiper.slideTo(index);
        }
    }

    customElements.define('rk-carousel-element', RkCarouselElement);
}
